import React from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  ShoppingBagIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  const stats = [
    {
      name: "Total Products",
      value: "24",
      change: "+12%",
      changeType: "positive",
      icon: ShoppingBagIcon,
    },
    {
      name: "Active Farms",
      value: "8",
      change: "+2",
      changeType: "positive",
      icon: BuildingOfficeIcon,
    },
    {
      name: "Pending Orders",
      value: "12",
      change: "-4%",
      changeType: "negative",
      icon: ClipboardDocumentListIcon,
    },
    {
      name: "Revenue",
      value: "$12,450",
      change: "+18%",
      changeType: "positive",
      icon: CurrencyDollarIcon,
    },
  ];

  const recentActivity = [
    {
      id: 1,
      type: "order",
      title: "New order from Green Valley Farm",
      description: "5 boxes of organic tomatoes",
      timestamp: "2 hours ago",
      icon: ClipboardDocumentListIcon,
    },
    {
      id: 2,
      type: "product",
      title: "New product added",
      description: "Fresh carrots from Sunny Acres",
      timestamp: "4 hours ago",
      icon: ShoppingBagIcon,
    },
    {
      id: 3,
      type: "delivery",
      title: "Delivery completed",
      description: "Order #1234 delivered successfully",
      timestamp: "6 hours ago",
      icon: TruckIcon,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            {" "}
            <h1 className="text-2xl font-bold">Welcome back, {user?.name}!</h1>
            <p className="mt-1 text-green-100">
              Here's what's happening with your AgriFlow business today.
            </p>
          </div>
          <div className="hidden md:block">
            <ChartBarIcon className="w-16 h-16 text-green-200" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {stat.name}
                </p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                  <p
                    className={`ml-2 text-sm font-medium ${
                      stat.changeType === "positive"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {stat.change}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <activity.icon className="w-5 h-5 text-gray-400 mt-1" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {activity.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {activity.description}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {activity.timestamp}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <ShoppingBagIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Add Product
              </span>
            </button>
            <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <BuildingOfficeIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Add Farm
              </span>
            </button>
            <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <ClipboardDocumentListIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                View Orders
              </span>
            </button>
            <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <ChartBarIcon className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Analytics
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
